import React, { useEffect, useRef, useState } from "react";
import { auth, db } from "../firebase";
import { ref, onValue, get } from "firebase/database";

const PRIMARY_TABS = (isDraftOver) => [
  { key: "matchup",   label: "Matchup"    },
  { key: "draft",     label: isDraftOver ? "Draft Recap" : "Draft" },
  { key: "lineup",    label: "Set Lineup" },
  { key: "myteam",    label: "My Team"    },
  { key: "season",    label: "Scoreboard" },
  { key: "standings", label: "Standings"  },
];

const MORE_TABS = () => [
  { key: "players",  label: "Players"  },
  { key: "waiver",   label: "Waivers"  },
  { key: "trades",   label: "Trades"   },
  { key: "playoffs", label: "Playoffs" },
  { key: "summary",  label: "Summary"  },
];

export function TopNav({ tab, setTab, disabled, leagueCode, isDraftOver }) {
  const [isHost,         setIsHost]         = useState(false);
  const [incomingTrades, setIncomingTrades] = useState(0);
  const [moreOpen,       setMoreOpen]       = useState(false);
  // Fixed-position coords for the dropdown so it escapes the nav's overflow clip
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, right: 0 });
  const moreRef       = useRef(null);
  const moreBtnRef    = useRef(null);
  const uid = auth.currentUser?.uid;

  useEffect(() => {
    if (!uid || !leagueCode) return;
    const code = leagueCode.toUpperCase();
    // Check both ownerUid (league) and hostId (room) — same criteria as ScoreEntryPage
    get(ref(db, `leagues/${code}/ownerUid`)).then(s => {
      if (s.val() === uid) setIsHost(true);
    });
    get(ref(db, `rooms/${code}/hostId`)).then(s => {
      if (s.val() === uid) setIsHost(true);
    });
    const u1 = onValue(ref(db, `rooms/${code}/slots`), snap => {
      const slots = snap.val() || {};
      const myEntry = Object.entries(slots).find(([, v]) => v?.claimedBy === uid);
      const mySlot = myEntry?.[0];
      if (!mySlot) return;
      const u2 = onValue(ref(db, `leagues/${code}/season/trades`), tradesSnap => {
        const trades = tradesSnap.val() || {};
        setIncomingTrades(Object.values(trades).filter(t => t.toTeam === mySlot && t.status === "pending").length);
      });
      return () => u2();
    });
    return () => { u1(); };
  }, [uid, leagueCode]);

  useEffect(() => {
    const handler = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const primaryTabs = PRIMARY_TABS(isDraftOver);
  const moreTabs = [
    ...MORE_TABS(),
    ...(isHost ? [
      { key: "commissioner", label: "⚙️ Commissioner" },
      { key: "scores",       label: "⚡ Score Entry"  },
    ] : []),
  ];

  const activeInMore = moreTabs.find(t => t.key === tab);
  const handleTab = (key) => { setTab(key); setMoreOpen(false); };

  const handleMoreToggle = () => {
    // Compute fixed position before opening so the dropdown escapes the nav's
    // overflow-x:auto clip (which forces overflow-y to non-visible).
    if (!moreOpen && moreBtnRef.current) {
      const r = moreBtnRef.current.getBoundingClientRect();
      setDropdownCoords({ top: r.bottom + 2, right: window.innerWidth - r.right });
    }
    setMoreOpen(v => !v);
  };

  return (
    <div className="league-nav">
      {primaryTabs.map(({ key, label }) => (
        <button
          key={key}
          className={`league-nav-tab ${tab === key ? "active" : ""}`}
          onClick={() => handleTab(key)}
          disabled={disabled}
          type="button"
        >
          {label}
        </button>
      ))}

      {/* More dropdown */}
      <div ref={moreRef} style={{ position: "relative" }} className="league-nav-more">
        <button
          ref={moreBtnRef}
          className={`league-nav-tab ${activeInMore ? "active" : ""}`}
          onClick={handleMoreToggle}
          disabled={disabled}
          type="button"
          style={{ position: "relative" }}
        >
          {activeInMore ? activeInMore.label : "More"} ▾
          {incomingTrades > 0 && (
            <span style={{
              position: "absolute", top: 6, right: 2,
              width: 14, height: 14, borderRadius: "50%",
              background: "var(--red)", color: "#fff",
              fontSize: 8, fontWeight: 900,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{incomingTrades}</span>
          )}
        </button>

        {moreOpen && (
          <div style={{
            position: "fixed", top: dropdownCoords.top, right: dropdownCoords.right,
            background: "var(--bg3)", border: "1px solid var(--border2)",
            borderRadius: "var(--radius)", zIndex: 9999,
            minWidth: 180, overflow: "hidden",
            boxShadow: "var(--shadow-lg)",
          }}>
            {moreTabs.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleTab(key)}
                type="button"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", padding: "10px 16px",
                  background: tab === key ? "rgba(245,166,35,0.1)" : "transparent",
                  color: tab === key ? "var(--gold)" : "var(--text)",
                  border: "none", borderBottom: "1px solid var(--border)",
                  fontFamily: "'DM Sans', sans-serif", fontSize: 13,
                  fontWeight: tab === key ? 700 : 500,
                  cursor: "pointer", textAlign: "left", borderRadius: 0,
                }}
                onMouseEnter={e => { if (tab !== key) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = tab === key ? "rgba(245,166,35,0.1)" : "transparent"; }}
              >
                {label}
                {key === "trades" && incomingTrades > 0 && (
                  <span style={{ width: 18, height: 18, borderRadius: "50%", background: "var(--red)", color: "#fff", fontSize: 9, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {incomingTrades}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
